import React, { useMemo, useState } from "react";
import { CheckCircle2, Loader2, Clock, Globe, ShieldCheck, Zap, User } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePageContent } from "@/lib/content";
import { Calendar } from "@/components/ui/calendar";

const COMMON_TIMEZONES = [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Toronto", "America/Sao_Paulo", "Europe/London", "Europe/Berlin",
    "Europe/Paris", "Europe/Amsterdam", "Asia/Dubai", "Asia/Kolkata",
    "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney", "UTC",
];

// Map deprecated IANA aliases → canonical modern names so the dropdown always finds a match
const TZ_ALIASES = {
    "Asia/Calcutta":   "Asia/Kolkata",
    "Asia/Katmandu":   "Asia/Kathmandu",
    "Asia/Saigon":     "Asia/Ho_Chi_Minh",
    "Asia/Ulaanbaatar":"Asia/Ulan_Bator",
    "America/Indiana/Indianapolis": "America/New_York",
};

function detectLocalTimezone() {
    const raw = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    return TZ_ALIASES[raw] || raw;
}

const PRACTICE_OPTIONS = [
    "Product Engineering", "Cloud & DevOps", "Data & AI",
    "Agentic AI", "Quality Engineering", "Security Engineering",
];

const INVOICE_VOLUMES = [
    "Under 500 invoices/month",
    "500 – 2,000 invoices/month",
    "2,000 – 10,000 invoices/month",
    "10,000 – 50,000 invoices/month",
    "50,000+ invoices/month",
    "Not sure yet",
];

function hashString(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}
function availableSlots(iso) {
    if (!iso) return [];
    const all = ["09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
        "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM"];
    const seed = hashString(iso);
    const bookedCount = 3 + (seed % 3);
    const booked = new Set();
    for (let i = 0; i < bookedCount; i++) booked.add(all[(seed >> (i * 2)) % all.length]);
    return all.filter((s) => !booked.has(s));
}
function toIso(d) {
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Right-panel info card — switches per active tab ─────────────────────────
function RightPanel({ state, date, time, tz }) {
    return (
        <div className="rounded-2xl bg-[hsl(222_47%_7%)] text-white p-7 lg:p-8 lg:sticky lg:top-6 space-y-7">

            {/* WHO YOU ARE MEETING */}
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-4">Who You Are Meeting</div>
                <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-full bg-primary/20 border border-primary/40 flex-shrink-0 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className="font-heading font-bold text-sm leading-snug text-white">{state.personTitle}</div>
                        <div className="text-xs text-white/55 mt-0.5">{state.personSub}</div>
                    </div>
                </div>
            </div>

            <div className="border-t border-white/10" />

            {/* WHAT TO EXPECT */}
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-4">{state.sessionLabel}</div>
                <ul className="space-y-3">
                    {state.agenda.filter(Boolean).map((item, i) => {
                        const match = item.match(/^\[?(\d+m)\]?\s+(.*)/);
                        return (
                            <li key={i} className="flex gap-3 items-start text-sm">
                                {match ? (
                                    <>
                                        <span className="text-primary font-bold text-xs mt-0.5 shrink-0 bg-primary/15 rounded px-1.5 py-0.5 min-w-[36px] text-center">{match[1]}</span>
                                        <span className="text-white/80 leading-snug">{match[2]}</span>
                                    </>
                                ) : (
                                    <span className="text-white/80 leading-snug">{item}</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="border-t border-white/10" />

            {/* TRUST + IMPACT */}
            <div className="space-y-3">
                <div className="flex items-start gap-2.5">
                    <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-primary/80 block mb-0.5">Trust Guarantee</span>
                        <span className="text-xs text-white/70">{state.trust}</span>
                    </div>
                </div>
                <div className="flex items-start gap-2.5">
                    <Zap className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-primary/80 block mb-0.5">Impact Proof</span>
                        <span className="text-xs text-white/70">{state.impact}</span>
                    </div>
                </div>
            </div>

            {/* Booking preview */}
            {date && time && (
                <>
                    <div className="border-t border-white/10" />
                    <div className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-3.5" data-testid="meeting-preview">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-primary/90">You&apos;re Booking</div>
                        <div className="mt-1.5 font-heading font-bold text-white text-sm">
                            {date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                        </div>
                        <div className="text-xs text-white/60 mt-0.5">{time} · {tz}</div>
                    </div>
                </>
            )}
        </div>
    );
}

const inputCls = "w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const labelCls = "text-sm font-medium text-foreground";
const stepHeadCls = "font-heading text-base font-bold text-foreground";

export default function ScheduleMeeting() {
    useSEO({ title: "Schedule a Meeting | RKS Systems", description: "Book a 30-minute conversation with an RKS Systems delivery leader." });
    const pc = usePageContent("schedule-meeting");

    // Tab state
    const [activeTab, setActiveTab] = useState("architecture");

    // Form state
    const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
    const [date, setDate] = useState(undefined);
    const [time, setTime] = useState("");
    const detectedTz = useMemo(() => detectLocalTimezone(), []);
    const [tz, setTz] = useState(detectedTz);
    // Build the dropdown list — always include the detected tz at the top if not already present
    const timezoneList = useMemo(() => (
        COMMON_TIMEZONES.includes(detectedTz) ? COMMON_TIMEZONES : [detectedTz, ...COMMON_TIMEZONES]
    ), [detectedTz]);
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", summary: "", topics: [], invoice_volume: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const iso = toIso(date);
    const slots = availableSlots(iso);
    const disableDate = (d) => {
        const dt = new Date(d); dt.setHours(0, 0, 0, 0);
        if (dt < today) return true;
        const dow = dt.getDay(); return dow === 0 || dow === 6;
    };
    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const toggleTopic = (t) => setForm(f => ({ ...f, topics: f.topics.includes(t) ? f.topics.filter(x => x !== t) : [...f.topics, t] }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            await api.post("/meetings", {
                name: form.name, company: form.company, email: form.email, phone: form.phone,
                summary: form.summary, preferred_date: iso, preferred_time: time, timezone: tz,
                meeting_type: activeTab,
                topics: activeTab === "architecture" ? form.topics : [],
                invoice_volume: activeTab === "invoiceos" ? form.invoice_volume : "",
            });
            setSuccess(true);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Something went wrong. Please try again.");
        } finally { setSubmitting(false); }
    };

    const canSubmit = date && time && form.name && form.email && form.summary && !submitting;

    // Tab labels
    const tab1Label = pc?.tab1_label || "Enterprise Engineering / Architecture Audit";
    const tab2Label = pc?.tab2_label || "InvoiceOS Product Demo & AP Automation";

    // State 1 — Enterprise Architecture (defaults match wireframe exactly)
    const state1 = {
        personTitle: pc?.s1_person_title || "Principal Systems Architect & Delivery Lead",
        personSub:   pc?.s1_person_sub   || "10+ Years Enterprise Production Tenure",
        sessionLabel: pc?.s1_session_label || "WHAT TO EXPECT IN YOUR 30-MINUTE SESSION",
        agenda: [
            pc?.s1_agenda_1 || "[03m] Context & Session Alignment",
            pc?.s1_agenda_2 || "[07m] Deep Dive: Architecture & Challenges",
            pc?.s1_agenda_3 || "[10m] Proven Engineering Patterns & HLD Case Studies",
            pc?.s1_agenda_4 || "[10m] Actionable Next Steps & Delivery Fit",
        ],
        trust:  pc?.s1_trust  || "100% NDA & 24-Hr HLD Proposal SLA",
        impact: pc?.s1_impact || "Enterprise Cloud & AI Modernization",
        buttonText:          pc?.s1_button_text          || "Confirm 30-Min Architecture Session",
        textareaPlaceholder: pc?.s1_textarea_placeholder || "A few sentences about your current system architecture, technical bottlenecks, or target engineering outcomes...",
    };

    // State 2 — InvoiceOS Demo (defaults match wireframe exactly)
    const state2 = {
        personTitle: pc?.s2_person_title || "Lead InvoiceOS Automation Solutions Architect",
        personSub:   pc?.s2_person_sub   || "Specialist in Accounts Payable AI",
        sessionLabel: pc?.s2_session_label || "WHAT TO EXPECT IN YOUR 30-MINUTE DEMO",
        agenda: [
            pc?.s2_agenda_1 || "[03m] AP Workflow & ERP Ecosystem Review",
            pc?.s2_agenda_2 || "[07m] Live Platform Demo: AI Extraction & Rules",
            pc?.s2_agenda_3 || "[10m] Custom Exception Handling & ERP Integration",
            pc?.s2_agenda_4 || "[10m] ROI Calculation, Pilot Plan & Pricing",
        ],
        trust:  pc?.s2_trust  || "Free 14-Day Sandbox / PoC Access",
        impact: pc?.s2_impact || "99.2% Accuracy, 80% Cost Reduction",
        buttonText:          pc?.s2_button_text          || "Request InvoiceOS Product Demo",
        textareaPlaceholder: pc?.s2_textarea_placeholder || "Describe your AP workflow challenges, ERP system, and current invoice volume or processing pain points...",
    };

    const activeState = activeTab === "architecture" ? state1 : state2;

    return (
        <div data-testid="page-schedule-meeting">
            {/* ── Hero ── */}
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                            {pc?.hero_headline || "Schedule Your 30-Minute Architecture Session or Product Demo"}
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            {pc?.hero_subheadline || "Direct access to RKS Systems Principal Engineers & Product Specialists. No sales pitches, complete privacy."}
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 lg:py-16 bg-secondary/30">
                <div className="container-app">

                    {/* ── Intent Tab Switcher ── */}
                    <div className="flex flex-wrap gap-3 mb-10" data-testid="meeting-tab-switcher">
                        {[
                            { id: "architecture", label: tab1Label },
                            { id: "invoiceos",    label: tab2Label },
                        ].map(tab => (
                            <button key={tab.id} type="button"
                                onClick={() => { setActiveTab(tab.id); setForm(f => ({ ...f, topics: [], invoice_volume: "" })); }}
                                data-testid={`meeting-tab-${tab.id}`}
                                className={`inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all ${
                                    activeTab === tab.id
                                        ? "border-primary bg-primary text-white shadow-sm"
                                        : "border-border bg-white text-foreground/70 hover:border-primary/40 hover:text-foreground"
                                }`}>
                                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${activeTab === tab.id ? "bg-white" : "bg-border"}`} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* ── Two-column layout ── */}
                    <div className="grid lg:grid-cols-[1fr,400px] gap-8 items-start">

                        {/* ── LEFT: Form ── */}
                        <div className="rounded-2xl border border-border bg-white p-7 lg:p-10">
                            {success ? (
                                <div className="text-center py-12" data-testid="meeting-success">
                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                                        <CheckCircle2 className="h-8 w-8" strokeWidth={1.75} />
                                    </div>
                                    <h2 className="mt-6 font-heading text-3xl font-bold">Session Confirmed!</h2>
                                    <p className="mt-3 text-muted-foreground max-w-sm mx-auto">
                                        You&apos;ll receive a calendar invite and confirmation within a few hours. Check your email.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-9" data-testid="meeting-form">

                                    {/* STEP 1 — Select Date & Local Time */}
                                    <div>
                                        <div className={stepHeadCls}>1. Select Date &amp; Local Time</div>
                                        <div className="mt-3 flex flex-wrap gap-3 items-start">
                                            <div>
                                                <label className="text-xs text-muted-foreground font-medium block mb-1.5">
                                                    <Globe className="h-3 w-3 inline mr-1" />Auto-detected Timezone
                                                </label>
                                                <select value={tz} onChange={e => setTz(e.target.value)} data-testid="meeting-timezone"
                                                    className="rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-60">
                                                    {timezoneList.map(z => <option key={z} value={z}>{z}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="mt-4 rounded-xl border border-border bg-white inline-block" data-testid="meeting-calendar">
                                            <Calendar mode="single" selected={date}
                                                onSelect={d => { setDate(d); setTime(""); }}
                                                disabled={disableDate} initialFocus />
                                        </div>
                                    </div>

                                    {/* STEP 2 — Select Available Time Slot */}
                                    <div>
                                        <div className={`${stepHeadCls} flex items-center gap-2`}>
                                            <Clock className="h-4 w-4 text-primary" strokeWidth={1.75} />
                                            2. Select Available Time Slot
                                        </div>
                                        {!date ? (
                                            <p className="mt-2 text-sm text-muted-foreground">Pick a date above to see available slots.</p>
                                        ) : slots.length === 0 ? (
                                            <p className="mt-2 text-sm text-muted-foreground">No available slots for this day. Please select another date.</p>
                                        ) : (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {slots.map(t => (
                                                    <button key={t} type="button" onClick={() => setTime(t)} data-testid={`meeting-time-${t}`}
                                                        className={`px-4 py-2 rounded-md text-sm border font-medium transition-colors ${time === t ? "border-primary bg-primary text-white" : "border-input bg-white hover:border-primary/50"}`}>
                                                        {t}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* STEP 3 — Essential Details */}
                                    <div>
                                        <div className={stepHeadCls}>3. Essential Details</div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelCls}>Your Name *</label>
                                                <input required value={form.name} onChange={change("name")} data-testid="meeting-name"
                                                    className={`mt-1.5 ${inputCls}`} placeholder="Your full name" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Work Email *</label>
                                                <input required type="email" value={form.email} onChange={change("email")} data-testid="meeting-email"
                                                    className={`mt-1.5 ${inputCls}`} placeholder="you@company.com" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Company Name</label>
                                                <input value={form.company} onChange={change("company")} data-testid="meeting-company"
                                                    className={`mt-1.5 ${inputCls}`} placeholder="Your company" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Phone Number <span className="text-muted-foreground font-normal">(Optional)</span></label>
                                                <input value={form.phone} onChange={change("phone")} data-testid="meeting-phone"
                                                    className={`mt-1.5 ${inputCls}`} placeholder="+1 555 000 0000" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* STEP 4 — Dynamic: switches based on active tab */}
                                    <div>
                                        {activeTab === "architecture" ? (
                                            <>
                                                <div className={stepHeadCls}>4. Primary Practices / Topics of Interest</div>
                                                <p className="mt-1 text-sm text-muted-foreground">Select all that apply to your engagement.</p>
                                                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                                    {PRACTICE_OPTIONS.map(opt => (
                                                        <label key={opt}
                                                            className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer text-sm font-medium transition-all ${
                                                                form.topics.includes(opt)
                                                                    ? "border-primary bg-primary/8 text-primary"
                                                                    : "border-border hover:border-primary/40"
                                                            }`}>
                                                            <input type="checkbox" className="sr-only" checked={form.topics.includes(opt)}
                                                                onChange={() => toggleTopic(opt)}
                                                                data-testid={`meeting-topic-${opt.replace(/\s+/g, "-").toLowerCase()}`} />
                                                            <span className={`h-3.5 w-3.5 rounded flex-shrink-0 border transition-colors ${form.topics.includes(opt) ? "bg-primary border-primary" : "border-input"}`} />
                                                            {opt}
                                                        </label>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className={stepHeadCls}>4. Monthly Invoice Volume</div>
                                                <p className="mt-1 text-sm text-muted-foreground">Helps us tailor the demo to your scale.</p>
                                                <select value={form.invoice_volume} onChange={change("invoice_volume")} data-testid="meeting-invoice-volume"
                                                    className={`mt-3 ${inputCls}`}>
                                                    <option value="">Select your monthly invoice volume...</option>
                                                    {INVOICE_VOLUMES.map(v => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </>
                                        )}
                                    </div>

                                    {/* STEP 5 — Context / Specific Pain Points */}
                                    <div>
                                        <div className={stepHeadCls}>5. Context / Specific Pain Points</div>
                                        <p className="mt-1 text-sm text-muted-foreground">Describe your requirement, problem, or challenges you&apos;d like to discuss.</p>
                                        <textarea required rows={4} value={form.summary} onChange={change("summary")} data-testid="meeting-summary"
                                            placeholder={activeState.textareaPlaceholder}
                                            className={`mt-3 ${inputCls}`} />
                                    </div>

                                    {error && (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="meeting-error">
                                            {error}
                                        </div>
                                    )}

                                    {/* Submit */}
                                    <div className="space-y-2.5">
                                        <button type="submit" disabled={!canSubmit} data-testid="meeting-submit"
                                            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 font-semibold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {submitting ? "Booking..." : `${activeState.buttonText} →`}
                                        </button>
                                        <p className="text-center text-xs text-muted-foreground">
                                            100% Confidential · NDA Protection Guaranteed
                                        </p>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* ── RIGHT: Dynamic Info Panel — switches per tab ── */}
                        <RightPanel state={activeState} date={date} time={time} tz={tz} />
                    </div>
                </div>
            </section>
        </div>
    );
}
